import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Slider,
  Paper,
  useTheme,
  alpha
} from '@mui/material'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInNew as PopoutIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material'
import { Document } from '../../types/api'
import Draggable from 'react-draggable'

interface DocumentViewerDialogProps {
  open: boolean
  document: Document | null
  blobUrl: string | null
  onClose: () => void
}

/**
 * DocumentViewerDialog - Popup redimensionabil și repositionabil pentru vizualizare documente
 * 
 * Features:
 * - Popup draggable 
 * - Resize manual din colțuri
 * - Zoom pentru imagini (scroll wheel + butoane)
 * - Dimensiune auto în funcție de tip document
 * - Suport dark mode
 * - Fără OCR
 */
const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  open,
  document,
  blobUrl,
  onClose
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { designSystem } = theme
  const colors = isDark ? designSystem.colors.dark : designSystem.colors.light

  // State pentru zoom (imagini)
  const [zoom, setZoom] = useState(100)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [size, setSize] = useState({ width: 800, height: 600 })
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null)
  const [isResizing, setIsResizing] = useState(false)
  
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const nodeRef = useRef<HTMLDivElement>(null)

  // Reset state când se deschide un document nou
  useEffect(() => {
    if (open && document) {
      setZoom(100)
      setImageNaturalSize(null)
      setIsFullscreen(false)
      
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      
      let initialWidth = 800
      let initialHeight = 600
      
      if (document.mime.includes('pdf')) {
        initialWidth = Math.min(1000, viewportWidth * 0.8)
        initialHeight = Math.min(800, viewportHeight * 0.85)
      } else if (document.mime.includes('image')) {
        initialWidth = Math.min(900, viewportWidth * 0.7)
        initialHeight = Math.min(700, viewportHeight * 0.8)
      } else {
        initialWidth = Math.min(800, viewportWidth * 0.6)
        initialHeight = Math.min(600, viewportHeight * 0.7)
      }
      
      setSize({ width: initialWidth, height: initialHeight })
      
      setPosition({
        x: (viewportWidth - initialWidth) / 2,
        y: Math.max(50, (viewportHeight - initialHeight) / 2 - 50)
      })
    }
  }, [open, document?.id])

  // Ajustează dimensiunea pentru imagini după ce se încarcă
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const naturalWidth = img.naturalWidth
    const naturalHeight = img.naturalHeight
    
    setImageNaturalSize({ width: naturalWidth, height: naturalHeight })
    
    // Ajustează dimensiunea popup-ului în funcție de imagine
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const maxWidth = viewportWidth * 0.85
    const maxHeight = viewportHeight * 0.85
    
    // Calculează dimensiunea optimă păstrând aspect ratio
    let newWidth = naturalWidth + 40 // padding
    let newHeight = naturalHeight + 120 // header + padding
    
    if (newWidth > maxWidth) {
      const ratio = maxWidth / newWidth
      newWidth = maxWidth
      newHeight = naturalHeight * ratio + 120
    }
    
    if (newHeight > maxHeight) {
      const ratio = maxHeight / newHeight
      newHeight = maxHeight
      newWidth = (newWidth - 40) * ratio + 40
    }
    
    // Minim 400x300
    newWidth = Math.max(400, newWidth)
    newHeight = Math.max(350, newHeight)
    
    setSize({ width: newWidth, height: newHeight })
    
    // Re-centrează
    setPosition({
      x: (viewportWidth - newWidth) / 2,
      y: Math.max(30, (viewportHeight - newHeight) / 2 - 30)
    })
  }, [])

  // Zoom handlers
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 400))
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25))
  const handleZoomReset = () => setZoom(100)

  // Wheel zoom pentru imagini
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (document?.mime.includes('image') && e.ctrlKey) {
      e.preventDefault()
      if (e.deltaY < 0) {
        handleZoomIn()
      } else {
        handleZoomOut()
      }
    }
  }, [document?.mime])

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, corner: string) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)

    const startX = e.clientX
    const startY = e.clientY
    const startWidth = size.width
    const startHeight = size.height
    const startPosX = position.x
    const startPosY = position.y

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX
      const deltaY = moveEvent.clientY - startY

      let newWidth = startWidth
      let newHeight = startHeight
      let newX = startPosX
      let newY = startPosY

      // Resize în funcție de colț
      if (corner.includes('e')) newWidth = Math.max(400, startWidth + deltaX)
      if (corner.includes('w')) {
        newWidth = Math.max(400, startWidth - deltaX)
        newX = startPosX + deltaX
      }
      if (corner.includes('s')) newHeight = Math.max(300, startHeight + deltaY)
      if (corner.includes('n')) {
        newHeight = Math.max(300, startHeight - deltaY)
        newY = startPosY + deltaY
      }

      setSize({ width: newWidth, height: newHeight })
      if (corner.includes('w') || corner.includes('n')) {
        setPosition({ x: newX, y: newY })
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
  }, [size, position])

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      setSize({ width: window.innerWidth - 40, height: window.innerHeight - 40 })
      setPosition({ x: 20, y: 20 })
    } else {
      // Restore previous size
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const newWidth = Math.min(800, viewportWidth * 0.7)
      const newHeight = Math.min(600, viewportHeight * 0.75)
      setSize({ width: newWidth, height: newHeight })
      setPosition({
        x: (viewportWidth - newWidth) / 2,
        y: (viewportHeight - newHeight) / 2
      })
    }
    setIsFullscreen(!isFullscreen)
  }

  // Download handler
  const handleDownload = () => {
    if (blobUrl && document) {
      const a = window.document.createElement('a')
      a.href = blobUrl
      a.download = document.file_name
      window.document.body.appendChild(a)
      a.click()
      window.document.body.removeChild(a)
    }
  }

  // Open in new tab
  const handleOpenInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, '_blank')
    }
  }

  const getDocumentKindLabel = (kind: string) => {
    const labels: { [key: string]: string } = {
      'generic': 'Generic',
      'referral': 'Bilet Trimitere',
      'consent': 'Consimțământ',
      'invoice': 'Factură',
      'export': 'Export',
      'medical_letter': 'Scrisoare Medicală',
      'external_result': 'Rezultat Extern',
      'external_report': 'Raport Extern',
      'external_image': 'Imagine Externă',
      'study_attachment': 'Atașament Studiu'
    }
    return labels[kind] || kind
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  // Resize handle component
  const ResizeHandle: React.FC<{ corner: string }> = ({ corner }) => {
    const cursorMap: { [key: string]: string } = {
      'nw': 'nw-resize', 'ne': 'ne-resize', 'sw': 'sw-resize', 'se': 'se-resize',
      'n': 'n-resize', 's': 's-resize', 'e': 'e-resize', 'w': 'w-resize'
    }
    
    const positionMap: { [key: string]: any } = {
      'nw': { top: 0, left: 0 },
      'ne': { top: 0, right: 0 },
      'sw': { bottom: 0, left: 0 },
      'se': { bottom: 0, right: 0 },
      'n': { top: 0, left: '50%', transform: 'translateX(-50%)' },
      's': { bottom: 0, left: '50%', transform: 'translateX(-50%)' },
      'e': { right: 0, top: '50%', transform: 'translateY(-50%)' },
      'w': { left: 0, top: '50%', transform: 'translateY(-50%)' }
    }

    const isCorner = ['nw', 'ne', 'sw', 'se'].includes(corner)
    
    return (
      <Box
        onMouseDown={(e) => handleResizeStart(e, corner)}
        sx={{
          position: 'absolute',
          ...positionMap[corner],
          width: isCorner ? 12 : (corner === 'n' || corner === 's' ? 60 : 8),
          height: isCorner ? 12 : (corner === 'e' || corner === 'w' ? 60 : 8),
          cursor: cursorMap[corner],
          zIndex: 10,
          '&:hover': {
            backgroundColor: alpha(colors.accent.primary, 0.3),
          }
        }}
      />
    )
  }

  if (!open) return null

  const isImage = document?.mime.includes('image')
  const isPdf = document?.mime.includes('pdf')

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha('#000', 0.5),
          zIndex: 1300,
        }}
      />

      {/* Draggable Popup */}
      <Draggable
        nodeRef={nodeRef}
        handle=".drag-handle"
        position={position}
        onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
        disabled={isResizing}
      >
        <Box
          ref={nodeRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1301,
            width: size.width,
            height: size.height,
          }}
        >
          <Paper
            ref={containerRef}
            elevation={24}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: colors.background.paper,
              border: `1px solid ${colors.border.default}`,
              position: 'relative',
            }}
          >
            {/* Resize handles */}
            <ResizeHandle corner="nw" />
            <ResizeHandle corner="ne" />
            <ResizeHandle corner="sw" />
            <ResizeHandle corner="se" />
            <ResizeHandle corner="n" />
            <ResizeHandle corner="s" />
            <ResizeHandle corner="e" />
            <ResizeHandle corner="w" />

            {/* Header - Drag Handle */}
            <Box
              className="drag-handle"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                backgroundColor: colors.background.elevated,
                borderBottom: `1px solid ${colors.border.default}`,
                cursor: 'move',
                userSelect: 'none',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <DragIcon sx={{ color: colors.text.secondary, fontSize: 20 }} />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600, 
                    color: colors.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                  title={document?.file_name}
                >
                  {document?.file_name || 'Document'}
                </Typography>
                {document && (
                  <>
                    <Chip
                      label={getDocumentKindLabel(document.kind)}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        backgroundColor: alpha(colors.accent.primary, 0.15),
                        color: colors.accent.primary,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                      {formatFileSize(document.size_bytes)}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Toolbar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isImage && (
                  <>
                    <Tooltip title="Zoom out (Ctrl+Scroll)">
                      <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 25}>
                        <ZoomOutIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        minWidth: 45, 
                        textAlign: 'center',
                        color: colors.text.secondary,
                        cursor: 'pointer',
                        '&:hover': { color: colors.accent.primary }
                      }}
                      onClick={handleZoomReset}
                      title="Click pentru reset"
                    >
                      {zoom}%
                    </Typography>
                    <Tooltip title="Zoom in (Ctrl+Scroll)">
                      <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 400}>
                        <ZoomInIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 1, height: 20, backgroundColor: colors.border.default, mx: 1 }} />
                  </>
                )}

                <Tooltip title="Deschide în tab nou">
                  <IconButton size="small" onClick={handleOpenInNewTab}>
                    <PopoutIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Descarcă">
                  <IconButton size="small" onClick={handleDownload}>
                    <DownloadIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={isFullscreen ? "Ieși din fullscreen" : "Fullscreen"}>
                  <IconButton size="small" onClick={toggleFullscreen}>
                    {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>

                <Box sx={{ width: 1, height: 20, backgroundColor: colors.border.default, mx: 0.5 }} />
                <Tooltip title="Închide (Esc)">
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: isImage ? 'flex-start' : 'stretch',
                backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5',
                p: isImage ? 2 : 0,
              }}
              onWheel={handleWheel}
            >
              {!blobUrl || !document ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Nu s-au putut încărca datele documentului
                  </Typography>
                </Box>
              ) : isPdf && document.is_xfa ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2, p: 4 }}>
                  <Typography variant="h6" color="text.secondary" textAlign="center">
                    Acest document este în format XFA și nu poate fi previzualizat în browser.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Descarcă fișierul și deschide-l cu Adobe Acrobat Reader.
                  </Typography>
                  <IconButton
                    onClick={() => {
                      const a = window.document.createElement('a')
                      a.href = blobUrl
                      a.download = document.file_name
                      a.click()
                    }}
                    sx={{
                      mt: 2,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <DownloadIcon sx={{ mr: 1 }} />
                    <Typography variant="button">Descarcă PDF</Typography>
                  </IconButton>
                </Box>
              ) : isPdf ? (
                <object
                  data={blobUrl}
                  type="application/pdf"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="PDF Viewer"
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, flexDirection: 'column', p: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      PDF-ul nu poate fi afișat în browser.
                    </Typography>
                  </Box>
                </object>
              ) : isImage ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    minHeight: '100%',
                    cursor: zoom > 100 ? 'grab' : 'default',
                  }}
                >
                  <img
                    ref={imageRef}
                    src={blobUrl}
                    alt={document.file_name}
                    onLoad={handleImageLoad}
                    draggable={false}
                    style={{
                      transform: `scale(${zoom / 100})`,
                      transformOrigin: 'center top',
                      maxWidth: zoom <= 100 ? '100%' : 'none',
                      transition: 'transform 0.15s ease',
                      borderRadius: 4,
                      boxShadow: isDark 
                        ? '0 4px 20px rgba(0,0,0,0.5)' 
                        : '0 4px 20px rgba(0,0,0,0.15)',
                    }}
                  />
                </Box>
              ) : (
                <iframe
                  src={blobUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="Document Viewer"
                />
              )}
            </Box>

            {/* Footer cu zoom slider pentru imagini */}
            {isImage && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  px: 3,
                  py: 1,
                  backgroundColor: colors.background.elevated,
                  borderTop: `1px solid ${colors.border.default}`,
                }}
              >
                <ZoomOutIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                <Slider
                  value={zoom}
                  onChange={(_, value) => setZoom(value as number)}
                  min={25}
                  max={400}
                  step={5}
                  sx={{ 
                    width: 200,
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                    }
                  }}
                />
                <ZoomInIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                <Typography variant="caption" sx={{ color: colors.text.secondary, minWidth: 40 }}>
                  {zoom}%
                </Typography>
                {imageNaturalSize && (
                  <Typography variant="caption" sx={{ color: colors.text.secondary, ml: 2 }}>
                    {imageNaturalSize.width} × {imageNaturalSize.height} px
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Draggable>
    </>
  )
}

export default DocumentViewerDialog
